import './applicantUnemploymentTypePage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { EmploymentType } from '@thesavingsgroup/enums/EmploymentType';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();

  const {
    computed: { isCurrentIncomePersisted },
    currentIncomeEmploymentType,
    isSyncingIncome,
    syncingIncomeFailedMessage,
    setCurrentIncomeEmploymentType,
    syncIncome,
  } = useApplicantStore();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      employmentTypeValue: currentIncomeEmploymentType,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<EmploymentType>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const employmentTypeValue = event.target.value;

    setIsInitialSelection(
      isEmpty(currentIncomeEmploymentType) && isValidClickEvent(event),
    );

    setCurrentIncomeEmploymentType(employmentTypeValue);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { employmentTypeValue } = values;

    if (!employmentTypeValue) {
      return;
    }

    if (isCurrentIncomePersisted && isDirty) {
      const synced = await syncIncome();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-unemployment-type-page" sx={theme.pageLayout}>
        <Header
          progress={progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applicantUnemploymentType.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantUnemploymentType.form.h1}>
                {t('ApplicantUnemploymentTypePage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantUnemploymentType.form.h2}>
                {t('ApplicantUnemploymentTypePage.subtitle')}
              </h2>

              {!isEmpty(syncingIncomeFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantUnemploymentType.alert.error
                    }
                    severity="error"
                  >
                    {syncingIncomeFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  name="employmentTypeValue"
                  options={[
                    {
                      label: t('ApplicantUnemploymentTypePage.disability'),
                      value: EmploymentType.DISABILITY,
                      testId:
                        locator.pages.applicantUnemploymentType.form
                          .radioOption1,
                    },
                    {
                      label: t('ApplicantUnemploymentTypePage.homemaker'),
                      value: EmploymentType.HOMEMAKER,
                      testId:
                        locator.pages.applicantUnemploymentType.form
                          .radioOption2,
                    },
                    {
                      label: t('ApplicantUnemploymentTypePage.retired'),
                      value: EmploymentType.RETIRED,
                      testId:
                        locator.pages.applicantUnemploymentType.form
                          .radioOption3,
                    },
                    {
                      label: t('ApplicantUnemploymentTypePage.student'),
                      value: EmploymentType.STUDENT,
                      testId:
                        locator.pages.applicantUnemploymentType.form
                          .radioOption4,
                    },
                    {
                      label: t('ApplicantUnemploymentTypePage.other'),
                      value: EmploymentType.OTHER,
                      testId:
                        locator.pages.applicantUnemploymentType.form
                          .radioOption5,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.applicantUnemploymentType.form.radioGroupRoot
                  }
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.applicantUnemploymentType.form.submitButton
                  }
                  disabled={isEmpty(currentIncomeEmploymentType)}
                  loader={isSyncingIncome}
                  fullWidth
                  submit
                >
                  {t('ApplicantUnemploymentTypePage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantUnemploymentType.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantUnemploymentTypePage';
